import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { UserCircle, UsersThree, Trash, Plus, LockKey } from "@phosphor-icons/react";

function apiError(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
}

export function MyDataCard({ user, onSaved }) {
  const [msg, setMsg] = useState(null); // {ok, text}
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const payload = {
      name: fd.get("name"),
      phone: fd.get("phone"),
      email: fd.get("email"),
    };
    const newPwd = fd.get("new_password");
    const curPwd = fd.get("current_password");
    if (newPwd) payload.new_password = newPwd;
    if (curPwd) payload.current_password = curPwd;
    setSaving(true);
    setMsg(null);
    try {
      await api.put("/auth/profile", payload);
      setMsg({ ok: true, text: "Datos actualizados correctamente" });
      e.target.new_password.value = "";
      e.target.current_password.value = "";
      onSaved && (await onSaved());
    } catch (err) {
      setMsg({ ok: false, text: apiError(err, "No se pudieron guardar los cambios") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="card" data-testid="my-data-form">
      <div className="flex items-center gap-2 mb-3">
        <UserCircle size={22} weight="duotone" style={{ color: "var(--primary)" }} />
        <h3 className="font-display text-lg font-bold">Mis datos</h3>
      </div>
      <div className="space-y-3">
        <div><label className="label">Nombre</label><input name="name" className="input-field" required defaultValue={user?.name} data-testid="profile-name-input" /></div>
        <div><label className="label">Teléfono</label><input name="phone" className="input-field" defaultValue={user?.phone || ""} data-testid="profile-phone-input" /></div>
        <div><label className="label">Email</label><input name="email" type="email" className="input-field" required defaultValue={user?.email} data-testid="profile-email-input" /></div>
      </div>
      <div className="flex items-center gap-2 mt-4 mb-1 text-slate-500 text-xs font-semibold">
        <LockKey size={14} weight="duotone" /> Para cambiar email o contraseña, introduce tu contraseña actual
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="label">Nueva contraseña</label><input name="new_password" type="password" className="input-field" placeholder="(opcional)" minLength={6} data-testid="profile-new-password-input" /></div>
        <div><label className="label">Contraseña actual</label><input name="current_password" type="password" className="input-field" data-testid="profile-current-password-input" /></div>
      </div>
      {msg && (
        <p className={`text-sm mt-3 font-semibold ${msg.ok ? "text-emerald-600" : "text-red-500"}`} data-testid="my-data-msg">{msg.text}</p>
      )}
      <button type="submit" disabled={saving} className="btn-primary w-full mt-4" data-testid="save-my-data-btn">
        {saving ? "Guardando…" : "Guardar mis datos"}
      </button>
    </form>
  );
}

export function VehicleReadOnly({ driverProfile }) {
  const rows = [
    ["Modelo", driverProfile?.vehicle_model],
    ["Placa", driverProfile?.vehicle_plate],
    ["Color", driverProfile?.vehicle_color],
    ["Bandera diurna", driverProfile?.base_fee_day != null ? `€${driverProfile.base_fee_day}` : null],
    ["Bandera nocturna", driverProfile?.base_fee_night != null ? `€${driverProfile.base_fee_night}` : null],
    ["€/km diurno", driverProfile?.rate_per_km_day != null ? `€${driverProfile.rate_per_km_day}` : null],
    ["€/km nocturno", driverProfile?.rate_per_km_night != null ? `€${driverProfile.rate_per_km_night}` : null],
  ];
  return (
    <div className="card" data-testid="vehicle-readonly">
      <div className="space-y-2 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between border-b border-slate-100 pb-1.5 last:border-0">
            <span className="text-slate-500">{k}</span>
            <span className="font-semibold">{v || "—"}</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500 mt-3">Solo el conductor titular puede editar el vehículo y las tarifas.</p>
    </div>
  );
}

export function AdditionalDriversCard() {
  const [drivers, setDrivers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/additional");
      setDrivers(data || []);
    } catch (err) { logger.warn("[driver] additional list failed", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    setSaving(true);
    setMsg(null);
    try {
      await api.post("/driver/additional", {
        name: fd.get("name"),
        email: fd.get("email"),
        phone: fd.get("phone") || null,
        password: fd.get("password"),
      });
      setMsg({ ok: true, text: "Conductor adicional creado. Ya puede iniciar sesión con su email y contraseña." });
      e.target.reset();
      setShowForm(false);
      await load();
    } catch (err) {
      setMsg({ ok: false, text: apiError(err, "No se pudo crear el conductor adicional") });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este conductor adicional? Ya no podrá iniciar sesión.")) return;
    try {
      await api.delete(`/driver/additional/${id}`);
      await load();
    } catch (err) {
      setMsg({ ok: false, text: apiError(err, "No se pudo eliminar") });
    }
  };

  return (
    <div className="card mt-4" data-testid="additional-drivers-card">
      <div className="flex items-center gap-2 mb-1">
        <UsersThree size={22} weight="duotone" style={{ color: "var(--primary)" }} />
        <h3 className="font-display text-lg font-bold">Conductores adicionales</h3>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Comparten tu vehículo y tarifas con su propio acceso (máx. 3). Solo uno puede estar en línea a la vez y cada viaje queda identificado por quién lo condujo.
      </p>

      {drivers.length === 0 && <p className="text-sm text-slate-500 mb-3" data-testid="no-additional-drivers">Sin conductores adicionales</p>}
      <div className="space-y-2 mb-3">
        {drivers.map((d) => (
          <div key={d.id} className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2.5" data-testid={`additional-driver-row-${d.id}`}>
            <div>
              <div className="font-bold text-sm">{d.name}</div>
              <div className="text-xs text-slate-500">{d.email}{d.phone ? ` · ${d.phone}` : ""}</div>
            </div>
            <button onClick={() => remove(d.id)} className="w-8 h-8 rounded-full bg-red-50 text-red-500 flex items-center justify-center" data-testid={`delete-additional-${d.id}`}>
              <Trash size={16} weight="duotone" />
            </button>
          </div>
        ))}
      </div>

      {msg && (
        <p className={`text-sm mb-3 font-semibold ${msg.ok ? "text-emerald-600" : "text-red-500"}`} data-testid="additional-msg">{msg.text}</p>
      )}

      {!showForm && drivers.length < 3 && (
        <button onClick={() => { setShowForm(true); setMsg(null); }} className="btn-ghost w-full flex items-center justify-center gap-2" data-testid="show-add-driver-btn">
          <Plus size={16} weight="bold" /> Agregar conductor adicional
        </button>
      )}

      {showForm && (
        <form onSubmit={add} className="space-y-3 border-t border-slate-100 pt-3" data-testid="add-driver-form">
          <div><label className="label">Nombre</label><input name="name" className="input-field" required data-testid="add-driver-name-input" /></div>
          <div><label className="label">Email (su usuario de acceso)</label><input name="email" type="email" className="input-field" required data-testid="add-driver-email-input" /></div>
          <div><label className="label">Teléfono</label><input name="phone" className="input-field" data-testid="add-driver-phone-input" /></div>
          <div><label className="label">Contraseña (mín. 6 caracteres)</label><input name="password" type="password" className="input-field" required minLength={6} data-testid="add-driver-password-input" /></div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost flex-1">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="add-driver-btn">{saving ? "Creando…" : "Crear acceso"}</button>
          </div>
        </form>
      )}
    </div>
  );
}
